import { Injectable } from "@angular/core";
import { ChecklistItem, ChecklistGroup, ToDoChecklistEntry, PendingChecklistSection } from "../interfaces/checklist.model";

@Injectable({
    providedIn: 'root'
})
export class ChecklistHelperService {

    buildHistoryDataForProvider(checkList: ChecklistItem[], data: any, isForms: boolean, isSelf: boolean): ChecklistGroup[] {
        const associations: Record<string, ChecklistItem[]> = {};

        if (checkList && checkList.length > 0) {
            checkList.forEach(checklist => {
                let key = 'Consumer';
                if (!isForms || (isForms && checklist.formMasterId != 0)) {
                    if (checklist.details && checklist.details.length > 0) {
                        if (checklist.details[0].associationId != -1) {
                            checklist.details.forEach(detail => {
                                key += '/' + this.normalizeTypeDesc(detail.typeDesc) + ' (' + detail.firstName + ' ' + detail.lastName + ' - ' + detail.pplId + ')';
                            });
                        }
                    }

                    if (!isSelf) {
                        checklist.details.push({
                            firstName: '',
                            lastName: '',
                            pplId: data.providerPPLID,
                            type: data.providerType,
                            typeDesc: data.providerTypeLabel,
                            associationId: data.participantProviderAssociationId,
                            relationship: data.relationship
                        });
                    }

                    if (!associations[key]) {
                        associations[key] = [];
                    }

                    associations[key].push(checklist);
                }
            });
            return this.buildHistoryDataForForms(associations);

        }
        return [];
    }

    buildHistoryDataForForms(associations: Record<string, ChecklistItem[]>): ChecklistGroup[] {
        const associationGroups: ChecklistGroup[] = [];

        Object.entries(associations).forEach(([key, values], index) => {
            this.resolveConditionalMandatory(values);
            values.forEach(v => {
                const data = v.data;
                const labelValue = data.filter(e => e.type == "label");
                const textValue = data.filter(e => e.type == "text");
                const fromData = data.filter(e => e.type == "fromDate");
                const toDate = data.filter(e => e.type == "toDate");
                const modifiedBy = data.filter(e => e.type == "modifiedByName");
                const modifiedDate = data.filter(e => e.type == "modifiedDate");
                const createdBy = data.filter(e => e.type == "createdByName");
                const createdDate = data.filter(e => e.type == "createdDate");
                const checklistId = data.filter(e => e.type == "checklistId");
                if (labelValue.length > 0) {
                    // EXCEPTION: conditionalMandatory.enableForEdit === true keeps the item editable
                    // even when the backend provides the value as a label (e.g. doc effective date).
                    v.readonly = !v.conditionalMandatory?.enableForEdit;
                    v.value = labelValue[0].value
                } else {
                    v.readonly = false;
                    v.value = textValue.length > 0 ? textValue[0].value : '';
                }
                // manualverfication === false → not user-fillable; force read-only.
                // EXCEPTION: conditionalMandatory.enableForEdit === true overrides this
                // (e.g. doc effective date that the backend marks non-fillable but is editable).
                if (v.manualverfication === false && !v.conditionalMandatory?.enableForEdit) {
                    v.readonly = true;
                }
                v.fromDate = fromData.length > 0 ? fromData[0].value : '';
                v.toDate = toDate.length > 0 ? toDate[0].value : '';
                v.modifiedBy = modifiedBy.length > 0 ? modifiedBy[0].value : '';
                v.modifiedDate = modifiedDate.length > 0 ? modifiedDate[0].value : '';
                v.createdBy = createdBy.length > 0 ? createdBy[0].value : '';
                v.createdDate = createdDate.length > 0 ? createdDate[0].value : '';
                v.checklistId = checklistId.length > 0 ? parseInt(checklistId[0].value) : undefined;
                if (v.fieldType == "label") {
                    const valueData = data.filter(e => e.type == "label");
                    v.value = valueData.length > 0 ? valueData[0].value : '';
                }

                if (v.fromDate && v.toDate && v.modifiedBy) {
                    v.fromDate = new Date(v.fromDate);
                    v.toDate = new Date(v.toDate);
                    v.dateRange = [v.fromDate, v.toDate];

                }
            });
            const mandatoryValues = values.filter(e => this.isEffectivelyMandatory(e));
            const mandatoryCompleted = mandatoryValues.filter(e => e.formStatus == 'C').length;
            const sortedItems = values.sort((a, b) => a.checklistOrder - b.checklistOrder);
            associationGroups.push({
                checklistName: '',
                items: values,
                children: [],
                isActive: false,
                totalItemsCompleted: mandatoryCompleted,
                isCompleted: mandatoryCompleted === mandatoryValues.length,
                totalItems: mandatoryValues.length
            });



        })
        this.sortByKey(associationGroups, 'checklistName');
        return associationGroups;
    }

    buildChecklistDataForParticipant(checklists: ChecklistItem[], isForms: boolean) {
        const associations: Record<string, ChecklistItem[]> = {};

        if (checklists && checklists.length > 0) {
            checklists.forEach(checklist => {
                let key = 'Participant';
                if (!isForms || (isForms && checklist.formMasterId != 0)) {
                    if (checklist.details && checklist.details.length > 0) {
                        if (checklist.details[0].associationId != -1) {
                            checklist.details.forEach(detail => {
                                key += '/' + this.normalizeTypeDesc(detail.typeDesc) + ' (' + detail.firstName + ' ' + detail.lastName + ' - ' + detail.pplId + ')';
                            });
                        }
                    }

                    if (!associations[key]) {
                        associations[key] = [];
                    }

                    associations[key].push(checklist);
                }
            });

            if (isForms) {
                return this.buildChecklistForForms(associations);
            } else {
                return this.buildChecklistFromAssocaitions(associations);
            }
        }
        return [];
    }

    buildChecklistDataForProvider(checklists: ChecklistItem[], data: any, isForms: boolean, isSelf: boolean) {
        const associations: Record<string, ChecklistItem[]> = {};

        if (checklists && checklists.length > 0) {
            checklists.forEach(checklist => {
                if (!isForms || (isForms && checklist.formMasterId != 0)) {
                    let key = isSelf ? 'Self' : '';
                    if (checklist.details && checklist.details.length > 0) {
                        checklist.details.forEach(detail => {
                            const normalized = this.normalizeTypeDesc(detail.typeDesc);
                            if (!isSelf) {
                                key += (key.length > 0 ? '/' : '') + normalized + ' (' + detail.firstName + ' ' + detail.lastName + ' - ' + detail.pplId + ')';
                            } else {
                                key += isSelf ? '/' + normalized : ' ' + normalized;
                            }
                        });
                    }

                    if (!isSelf) {
                        checklist.details.push({
                            firstName: '',
                            lastName: '',
                            pplId: data.providerPPLID,
                            type: data.providerType,
                            typeDesc: data.providerTypeLabel,
                            associationId: data.participantProviderAssociationId,
                            relationship: data.relationship
                        });
                    }

                    if (!associations[key]) {
                        associations[key] = [];
                    }

                    associations[key].push(checklist);
                }
            });

            if (isForms) {
                return this.buildChecklistForForms(associations);
            } else {
                return this.buildChecklistFromAssocaitions(associations);
            }
        }

        return [];
    }

    buildChecklistFromAssocaitions(associations: Record<string, ChecklistItem[]>) {
        const associationGroups: ChecklistGroup[] = [];
        let groups: ChecklistGroup[] = [];
        let subGroups: ChecklistGroup[] = [];

        Object.entries(associations).forEach(([key, values], index) => {
            this.resolveConditionalMandatory(values);
            const categoryGroup = this.groupByKey(values, 'category');
            groups = [];
            Object.entries(categoryGroup).forEach(([group, categoryItems], groupIndex) => {
                const subCategory = this.groupByKey(categoryItems, 'subCategory');

                subGroups = [];
                let sameAsCategory: ChecklistItem[] = [];
                Object.entries(subCategory).forEach(([subGroup, subCategoryItems], subGroupIndex) => {
                    const items = subCategoryItems as ChecklistItem[];
                    sameAsCategory = sameAsCategory.concat(items.filter(e => e.sameAsCategory));
                    const newSubCategories = items.filter(e => !e.sameAsCategory);
                    if (newSubCategories.length > 0) {
                        const sortedSubCategories = newSubCategories.sort((a, b) => a.checklistOrder - b.checklistOrder);
                        sortedSubCategories.forEach(item => {
                            const data = item.data;
                            const labelValue = data.filter(e => e.type == "label");
                            const textValue = data.filter(e => e.type == "text");
                            const fromData = data.filter(e => e.type == "fromDate");
                            const toDate = data.filter(e => e.type == "toDate");

                            if (labelValue.length > 0) {
                                // EXCEPTION: conditionalMandatory.enableForEdit === true keeps the item editable
                                // even when the backend provides the value as a label (e.g. doc effective date).
                                item.readonly = !item.conditionalMandatory?.enableForEdit;
                                item.value = labelValue[0].value
                            } else {
                                item.readonly = false;
                                item.value = textValue.length > 0 ? textValue[0].value : '';
                            }
                            // manualverfication === false → not user-fillable; force read-only.
                            // EXCEPTION: conditionalMandatory.enableForEdit === true overrides this
                            // (e.g. doc effective date that the backend marks non-fillable but is editable).
                            if (item.manualverfication === false && !item.conditionalMandatory?.enableForEdit) {
                                item.readonly = true;
                            }
                            item.fromDate = fromData.length > 0 ? fromData[0].value : '';
                            item.toDate = toDate.length > 0 ? toDate[0].value : '';

                            if (item.fromDate && item.toDate) {
                                item.fromDate = new Date(item.fromDate);
                                item.toDate = new Date(item.toDate);
                                item.dateRange = [item.fromDate, item.toDate];
                            }
                        });
                        const mandatoryItems = newSubCategories.filter(e => this.isEffectivelyMandatory(e));
                        const mandatoryCompleted = mandatoryItems.filter(e => this.isItemCompleted(e)).length;
                        subGroups.push({
                            checklistName: subGroup,
                            children: [],
                            items: sortedSubCategories,
                            isActive: false,
                            totalItemsCompleted: mandatoryCompleted,
                            isCompleted: mandatoryCompleted === mandatoryItems.length,
                            totalItems: mandatoryItems.length
                        });
                    }
                });

                const sortedCategoryItems = sameAsCategory.sort((a, b) => a.checklistOrder - b.checklistOrder);
                sortedCategoryItems.forEach(item => {
                    const data = item.data;
                    const labelValue = data.filter(e => e.type == "label");
                    const textValue = data.filter(e => e.type == "text");
                    const fromData = data.filter(e => e.type == "fromDate");
                    const toDate = data.filter(e => e.type == "toDate");
                    if (labelValue.length > 0) {
                        // EXCEPTION: conditionalMandatory.enableForEdit === true keeps the item editable
                        // even when the backend provides the value as a label (e.g. doc effective date).
                        item.readonly = !item.conditionalMandatory?.enableForEdit;
                        item.value = labelValue[0].value
                    } else {
                        item.readonly = false;
                        item.value = textValue.length > 0 ? textValue[0].value : '';
                    }
                    // manualverfication === false → not user-fillable; force read-only.
                    // EXCEPTION: conditionalMandatory.enableForEdit === true overrides this
                    // (e.g. doc effective date that the backend marks non-fillable but is editable).
                    if (item.manualverfication === false && !item.conditionalMandatory?.enableForEdit) {
                        item.readonly = true;
                    }
                    item.fromDate = fromData.length > 0 ? fromData[0].value : '';
                    item.toDate = toDate.length > 0 ? toDate[0].value : '';

                    if (item.fieldType == "label") {
                        const valueData = data.filter(e => e.type == "label");
                        item.value = valueData.length > 0 ? valueData[0].value : '';
                    }

                    if (item.fromDate && item.toDate) {
                        item.fromDate = new Date(item.fromDate.replace(/-/g, '/'));
                        item.toDate = new Date(item.toDate.replace(/-/g, '/'));
                        item.dateRange = [item.fromDate, item.toDate];
                    }
                });
                const mandatorySameAsCategory = sameAsCategory.filter(e => this.isEffectivelyMandatory(e));
                const totalSubGroupsCompleted = subGroups.filter(e => e.isCompleted).length + mandatorySameAsCategory.filter(e => this.isItemCompleted(e)).length;
                const totalMandatoryCategoryItems = subGroups.length + mandatorySameAsCategory.length;
                groups.push({
                    checklistName: group,
                    children: subGroups,
                    items: sortedCategoryItems,
                    isActive: false,
                    totalItemsCompleted: totalSubGroupsCompleted,
                    totalItems: totalMandatoryCategoryItems,
                    isCompleted: totalSubGroupsCompleted === totalMandatoryCategoryItems
                });
            });

            const totalAssociatedGroupsCompleted = groups.filter(e => e.isCompleted).length;
            associationGroups.push({
                checklistName: key,
                children: groups,
                items: [],
                isActive: false,
                totalItemsCompleted: totalAssociatedGroupsCompleted,
                isCompleted: totalAssociatedGroupsCompleted == groups.length,
                totalItems: groups.length
            });
        });


        this.sortByKey(associationGroups, 'checklistName');
        return associationGroups;
    }

    buildChecklistForForms(associations: Record<string, ChecklistItem[]>) {
        const associationGroups: ChecklistGroup[] = [];

        Object.entries(associations).forEach(([key, values], index) => {
            this.resolveConditionalMandatory(values);
            values.forEach(v => {
                v.status = v.formStatus == 'C' ? 'Completed' : 'Not Completed';
            });
            const mandatoryValues = values.filter(e => this.isEffectivelyMandatory(e));
            const mandatoryCompleted = mandatoryValues.filter(e => e.formStatus == 'C').length;
            const sortedItems = values.sort((a, b) => a.checklistOrder - b.checklistOrder);
            associationGroups.push({
                checklistName: key,
                items: sortedItems,
                children: [],
                isActive: false,
                totalItemsCompleted: mandatoryCompleted,
                isCompleted: mandatoryCompleted === mandatoryValues.length,
                totalItems: mandatoryValues.length
            });
        });


        this.sortByKey(associationGroups, 'checklistName');
        return associationGroups;
    }

    buildToDoChecklist(checklists: ChecklistItem[], isParticipantFlow: boolean, isSelf: boolean): ToDoChecklistEntry[] {
        const pendingToDoItems: ToDoChecklistEntry[] = [];
        const associations: Record<string, ChecklistItem[]> = {};

        if (checklists && checklists.length > 0) {
            checklists.forEach(checklist => {
                let key = !isParticipantFlow && isSelf ? 'Self' : '';
                if (checklist.details && checklist.details.length > 0) {
                    checklist.details.forEach(detail => {
                        key += key.length > 0 ? '/' : '';
                        const typeDesc = this.normalizeTypeDesc(detail.typeDesc);
                        if (isParticipantFlow) {
                            key += typeDesc + ' (' + detail.firstName + ' ' + detail.lastName + ' - ' + detail.pplId + ')';
                        } else {
                            key += typeDesc;
                        }
                    });
                }

                if (isParticipantFlow && key === '') {
                    key = 'Participant';
                }

                if (!associations[key]) {
                    associations[key] = [];
                }

                associations[key].push(checklist);
            });

            Object.entries(associations).forEach(([key, values], index) => {
                this.resolveConditionalMandatory(values);
                const mandatoryValues = values.filter(e => this.isEffectivelyMandatory(e));

                if (mandatoryValues.length > 0) {
                    const categoryGroup = this.groupByKey(mandatoryValues, 'category');
                    Object.entries(categoryGroup).forEach(([subGroup, categoryItems]) => {
                        pendingToDoItems.push({
                            name: subGroup,
                            for: key,
                            questions: this.toQuestionList(categoryItems as ChecklistItem[])
                        });
                    });
                }
            });
        }

        return pendingToDoItems;
    }

    /**
     * Per-category lists of the required items still pending inside a built association checklist
     * group, for display beneath its To-Do entry. Items a category owns directly and those under
     * its subGroups are listed together under that category, and categories with nothing pending
     * are dropped.
     */
    buildPendingSections(group: ChecklistGroup, extraItems: ChecklistItem[] = []): PendingChecklistSection[] {
        const byCategory = new Map<string, ChecklistItem[]>();

        (group.children || []).forEach(category => {
            byCategory.set(category.checklistName, this.collectPendingRequired(category));
        });

        // Items held out of the group tree because they carry their own To-Do entry (On-Duty Meal
        // Break, HCBS) are folded back into their own category so the list is complete.
        extraItems.filter(item => this.isPendingRequired(item)).forEach(item => {
            byCategory.set(item.category, [...(byCategory.get(item.category) ?? []), item]);
        });

        return [...byCategory.entries()]
            .map(([category, items]) => ({ category, questions: this.toQuestionList(items) }))
            .filter(section => section.questions.length > 0);
    }

    private collectPendingRequired(category: ChecklistGroup): ChecklistItem[] {
        const items = [
            ...(category.items || []),
            ...(category.children || []).flatMap(subGroup => subGroup.items || [])
        ];
        return items.filter(item => this.isPendingRequired(item));
    }

    private isPendingRequired(item: ChecklistItem): boolean {
        return !item.hide && this.isEffectivelyMandatory(item) && !this.isItemCompleted(item);
    }

    /** Question text for the given items, in the same order the checklist screens list them. */
    private toQuestionList(items: ChecklistItem[]): string[] {
        return [...items]
            .sort((a, b) => a.checklistOrder - b.checklistOrder)
            .map(item => item.question);
    }

    /**
     * Builds the display label for a form-based to-do item from its description. The template
     * prefixes "Form", so any trailing "Form" in the description is stripped to avoid duplication
     * (e.g. "HCBS Form" -> "HCBS", rendered as "Form HCBS ...").
     */
    buildFormTodoLabel(formDesc: string): string {
        return (formDesc || '').replace(/\s*form\s*$/i, '').trim();
    }

    /**
     * Resolves effective mandatory status for items with conditionalMandatory.
     * If an item has conditionalMandatory and the dependent item's value matches expectedValue,
     * the item is treated as mandatory.
     */
    private resolveConditionalMandatory(items: ChecklistItem[]): void {
        const itemMap = new Map<number, ChecklistItem>();
        items.forEach(item => itemMap.set(item.checklistItemId, item));

        items.forEach(item => {
            if (item.conditionalMandatory && !item.mandatory) {
                const cm = item.conditionalMandatory;
                let conditionMet = false;

                if (cm.dependencies && cm.dependencies.length > 0) {
                    const results = cm.dependencies.map(dep => {
                        const depItem = itemMap.get(dep.dependsOnChecklistItemId);
                        const valueData = depItem?.data?.find(e => e.type === 'label' || e.type === 'text');
                        return valueData?.value === dep.expectedValue;
                    });
                    conditionMet = cm.operator === 'OR'
                        ? results.some(r => r)
                        : results.every(r => r);
                } else if (cm.dependsOnChecklistItemId != null) {
                    const dependentItem = itemMap.get(cm.dependsOnChecklistItemId);
                    if (dependentItem) {
                        const valueData = dependentItem.data?.find(e => e.type === 'label' || e.type === 'text');
                        conditionMet = !!(valueData && valueData.value === cm.expectedValue);
                    }
                }

                item.isConditionallyMandatory = conditionMet && cm.mandatory === true;
            }
        });
    }

    private isEffectivelyMandatory(item: ChecklistItem): boolean {
        return item.mandatory || item.isConditionallyMandatory === true;
    }

    private isItemCompleted(item: ChecklistItem): boolean {
        return item.checkListStatus === 'C';
    }

    private normalizeTypeDesc(typeDesc: string): string {
        if (typeDesc === 'Personal Assistant') return 'Independent Provider';
        if (typeDesc === 'Consumer') return 'Participant';
        return typeDesc;
    }

    sortByKey(array: any, key: string) {
        array.sort((a: any, b: any) => {
            const titleA = a[key].toLowerCase();
            const titleB = b[key].toLowerCase();
            if (titleA < titleB) {
                return -1;
            }
            if (titleA > titleB) {
                return 1;
            }

            // names must be equal
            return 0;
        });
    }

    groupByKey(array: any, key: string) {
        return array
            .reduce((hash: any, obj: any) => {
                if (obj[key] === undefined) return hash;
                return Object.assign(hash, { [obj[key]]: (hash[obj[key]] || []).concat(obj) })
            }, {})
    }
}